import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, PlayCircle, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Job {
  id: string;
  title: string;
  status: string;
  customer_id: string;
  tradesman_id?: string;
}

interface JobStatusManagerProps {
  job: Job;
  onJobUpdate: () => void;
  userType: 'customer' | 'tradesman';
}

export const JobStatusManager = ({ job, onJobUpdate, userType }: JobStatusManagerProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const { toast } = useToast();

  // Get current user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("id, user_type")
            .eq("user_id", user.id)
            .single();
          
          console.log("JobStatusManager - Profile fetched:", profile);
          console.log("JobStatusManager - Job:", job);
          console.log("JobStatusManager - UserType:", userType);
          
          if (profile) {
            setCurrentUserProfile(profile);
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    
    fetchProfile();
  }, []);

  const canMarkOngoing = () => {
    if (!currentUserProfile) {
      console.log("JobStatusManager - No current user profile");
      return false;
    }
    
    const canMark = job.status === 'assigned' && (
      (userType === 'customer' && job.customer_id === currentUserProfile.id) ||
      (userType === 'tradesman' && job.tradesman_id === currentUserProfile.id)
    );
    
    console.log("JobStatusManager - canMarkOngoing:", {
      jobStatus: job.status,
      userType,
      profileId: currentUserProfile.id,
      customerId: job.customer_id,
      tradesmanId: job.tradesman_id,
      canMark
    });
    
    return canMark;
  };

  const canMarkCompleted = () => {
    if (!currentUserProfile) return false;
    
    return (job.status === 'in_progress' || job.status === 'assigned') && 
           userType === 'customer' && 
           job.customer_id === currentUserProfile.id;
  };

  const updateJobStatus = async (newStatus: string) => {
    setIsUpdating(true);
    try {
      console.log("JobStatusManager - Updating job status to:", newStatus);
      
      const { error } = await supabase
        .from("jobs")
        .update({ status: newStatus })
        .eq("id", job.id);

      if (error) {
        console.error("JobStatusManager - Update error:", error);
        throw error;
      }

      console.log("JobStatusManager - Job status updated successfully");

      // If job is marked as completed, send notification emails
      if (newStatus === 'completed') {
        console.log("JobStatusManager - Sending job completion notifications");
        try {
          const { error: emailError } = await supabase.functions.invoke('notify-job-completion', {
            body: { job_id: job.id }
          });

          if (emailError) {
            console.error("Error sending completion notifications:", emailError);
            // Don't throw here as the job status was already updated successfully
          } else {
            console.log("Job completion notifications sent successfully");
          }
        } catch (emailError) {
          console.error("Error sending completion notifications:", emailError);
          // Don't throw here as the job status was already updated successfully
        }
      }

      toast({
        title: "Job status updated",
        description: `Job marked as ${newStatus.replace('_', ' ')}${newStatus === 'completed' ? '. Completion notifications sent to all parties.' : ''}`,
      });

      onJobUpdate();
    } catch (error: any) {
      console.error("JobStatusManager - Error updating job status:", error);
      toast({
        title: "Error updating job status",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "assigned": return "bg-blue-100 text-blue-800";
      case "in_progress": return "bg-orange-100 text-orange-800";
      case "completed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex gap-2 mt-3">
      {/* Mark as Ongoing Button */}
      {canMarkOngoing() && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" disabled={isUpdating} className="text-black border-black hover:bg-black hover:text-white">
              <PlayCircle className="h-4 w-4 mr-1" />
              Start Work
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark Job as Ongoing</AlertDialogTitle>
              <AlertDialogDescription>
                This will change the job status to "In Progress" and indicate that work has started on "{job.title}".
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => updateJobStatus('in_progress')}>
                Start Work
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Mark as Completed Button */}
      {canMarkCompleted() && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" disabled={isUpdating} className="text-black">
              <CheckCircle className="h-4 w-4 mr-1" />
              Mark Complete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark Job as Completed</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark "{job.title}" as completed. This action indicates that all work has been finished to your satisfaction.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => updateJobStatus('completed')}>
                Mark Complete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};